import Head from 'next/head';
import { request } from 'graphql-request';
import dynamic from 'next/dynamic';
import { useAmp } from 'next/amp';
import SiteSettings from '~/siteSettings';
import BlockParser from '~/components/blockParser/blockParser';
import BlockVideos from '~/components/blockVideos/blockVideos';
import InjectedStylesCatcher from '~/components/injectedStylesCatcher/injectedStylesCatcher';
import PageWrapper from '~/components/wrappers/pageWrapper';
import VideoBlock from '~/components/videoBlock/videoBlock';
import {
  getStructuredData,
  getContentPaths,
  getAlias,
  getBlocksFromQueue,
  getDrupalSettings,
  getRecommendations
} from '~/utils/utils';

// AMP
import VideoAmp from './videoAmp';

import GET_VIDEO from '~/graphql/GET_VIDEO.graphql';

const Utag = dynamic(() => import('~/components/utag/utag'), { ssr: false });
const BidBarrelReact = dynamic(() => import('~/components/bidBarrelReact/bidBarrelReact'), { ssr: false });

/**
 * Video
 * @category Page
 * @extends Component
 * @summary Display the Video page.
 * @return {Node} React node containing the Video page.
 */

const Video = props => {
  const isAmp = useAmp();
  const {
    sitePrimaryRsid,
    baseUrl,
    router,
    fileUrl,
    data,
    blocks,
    drupalSettings,
    recommendations
  } = props;
  const blockItems = blocks?.entitySubQueueQuery?.items;
  const path = router?.query?.alias
    ? `/videos/${ router.query.alias.join('/') }`
    : '/';
  const content = {
    ...data?.nodeQuery,
    bundle: router?.query?.bundle
  };
  const keywords = content.field_tags && content.field_tags.map(tag => tag.name).join(',');

  return (
    <>
      <Head>
        <title>{ content.title } | { SiteSettings.siteName }</title>
        <meta name='title' content={ content.title } />
        <link rel='canonical' href={`${ baseUrl }${ path }`} />
        <meta name='twitter:title' content={ content.field_social_headline || content.title } />
        <meta name='description' content={ content.field_seo_description || content.field_subhead } />
        <meta property='og:url' content={ path } />
        <meta property='og:title' content={ content.field_social_headline || content.title } />
        <meta property='og:image' content={ content?.field_image?.field_image?.social?.url } />
        <meta property='og:description' content={ content.field_social_description || content.field_subhead } />
        <link rel='image_src' href={ content?.field_image?.field_image?.social?.url } />
        <meta name='twitter:url' content={ path } />
        <meta name='twitter:image:width' content={ content?.field_image?.field_image?.social?.width } />
        <meta name='twitter:image:height' content={ content?.field_image?.field_image?.social?.height } />
        <meta name='twitter:image' content={ content?.field_image?.field_image?.social?.url } />
        <meta property='article:published_time' content={ content.field_display_date } />
        <meta name='keywords' content={ keywords || SiteSettings.defaultKeywords } />
        <meta name='robots' content={`${(content.field_is_promotional || content.field_is_unlisted) ? 'noindex' : 'index'}, max-image-preview:large`} />
      </Head>
      { !isAmp &&
        <Head>
          <link rel='amphtml' href={`${ baseUrl }${ path }?amp=1`} />
          <script
            key='json-ld'
            type='application/ld+json'
            dangerouslySetInnerHTML={{ __html: JSON.stringify(getStructuredData(baseUrl, path, content)) }}
          />
        </Head>
      }
      {/* AMP */}
      { isAmp &&
        <VideoAmp
          drupalSettings={ drupalSettings }
          data={ data }
          pageType='videos'
          siteSection='videos'
          keywords={ keywords || SiteSettings.defaultKeywords }
        />
      }
      {/* Non-AMP */}
      { !isAmp &&
        <>
          <InjectedStylesCatcher />
          <VideoBlock
            data={ data }
            fileUrl={ fileUrl }
            drupalSettings={ drupalSettings }
            parentNid={ content.nid }
            pageType='video'
            section='video'
            recommendations={ recommendations }
          />
          {/* Related Content */}
          {/* @NOTE: DREW-286 is tracking an issue when selecting duration, it is omitted for the time being */}
          { content.field_related_content && content.field_related_content.length > 0 &&
            <>
              <div className='wrap-bg-primary'>
                <BlockVideos
                  items={ content.field_related_content }
                  title='related videos'
                  seeMore
                  seeMoreAt={ 2 }
                />
              </div>
              <div data-ad-ref='leader-plus-flex-middle' className='bidbarrel-ad'></div>
              <div data-ad-ref='mobile-plus-flex-middle' className='bidbarrel-ad'></div>
            </>
          }
          { blockItems &&
            <BlockParser blockItems={ blockItems } />
          }
          <div data-ad-ref='leader-plus-flex-bottom' className='bidbarrel-ad'></div>
          <div data-ad-ref='mobile-plus-flex-bottom' className='bidbarrel-ad'></div>
          <Utag
            pageType='video'
            siteSection='videos'
            data={{
              sitePrimaryRsid,
              assetType: 'video',
              editorialContentName: content.title,
              editorialContentId: content.nid,
              videoTitle: content?.title,
              ...(keywords && {keywords})
            }}
          />
          <BidBarrelReact
            data={ content }
            mapToAdUnits={{
              mobile: [
                'mobile-omni-skybox-plus-sticky',
                'mobile-plus-flex-top',
                'mobile-plus-flex-middle',
                'mobile-plus-flex-inc',
                'mobile-plus-flex-bottom'
              ],
              desktop: [
                'omni-skybox-plus-sticky',
                'leader-plus-flex-top',
                'leader-plus-flex-middle',
                'leader-plus-flex-inc',
                'leader-plus-flex-bottom'
              ]
            }}
          />
        </>
      }
    </>
  );
}

export async function getStaticPaths() {
  const paths = await getContentPaths('/videos/' ,{
    machine_name: 'latest_content_by_type',
    display: 'master',
    itemsPerPage: parseInt(process.env.PRERENDER_COUNT, 10) || 10,
    offset: 0,
    viewArgs: [
      'video'
    ]
  });

  return {
    paths,
    fallback: 'blocking'
  };
}

export async function getStaticProps(ctx) {
  // Get Alias
  const aliasData = await getAlias({
    alias: `/videos/${ ctx.params.alias.join('/') }`
  });

  if(!aliasData?.pathAliasQuery?.nid) {
    return {
      notFound: true,
      revalidate: 1
    }
  }

  let data = {};
  let recommendations = {};

  // Get Data
  data = await request(process.env.NEXT_PUBLIC_API_HOST || 'https://api-stage.thedrewbarrymoreshow.com/cmv_graphql/api/query', GET_VIDEO, {
    target_id: parseInt(aliasData.pathAliasQuery.nid, 10)
  });

  if(!data?.nodeQuery) {
    return {
      notFound: true,
      revalidate: 1
    }
  }

  // Get Recomendations
  recommendations = await getRecommendations();

  // Get Blocks
  const blocks = await getBlocksFromQueue({
    target_id: 'video_blocks',
    limit: 100
  });

  // Get Site Settings
  const drupalSettings = await getDrupalSettings();

  return {
    props: {
      data,
      blocks,
      drupalSettings,
      recommendations
    },
    revalidate: 1
  }
}

export const config = { amp: 'hybrid' };

export default PageWrapper(Video);